'use client';

import { createContext, useContext, useMemo } from 'react';
import { useGetFilterSetByIdQuery } from 'src/api/redux/filterSets';
import type { ProfileConfigWidget } from 'src/api/redux/profileConfiguration/types';
import {
  MIN_VOLUME_YEAR,
  MAX_VOLUME_YEAR,
} from 'src/components/constants';

interface WidgetConfigContextValue {
  widget: ProfileConfigWidget;
  filterSet: MedScout.FilterSet | null;
  filterSetId: number | null;
  /** The provider filter set ID (widget.filter_set.id), distinct from the code set. */
  providerFilterSetId: number | null;
  isLoading: boolean;
  parentLabel?: string;
  parentSummary?: string;
  /** Formatted year range from widget config, e.g. "2023 - 2025" */
  dateRangeLabel: string;
  minYear: number;
  maxYear: number;
}

const WidgetConfigContext = createContext<WidgetConfigContextValue | null>(null);

export const useWidgetConfig = () => useContext(WidgetConfigContext);

interface WidgetConfigProviderProps {
  widget: ProfileConfigWidget;
  parentLabel?: string;
  parentSummary?: string;
  children: React.ReactNode;
}

export const WidgetConfigProvider: React.FC<WidgetConfigProviderProps> = ({
  widget,
  parentLabel,
  parentSummary,
  children,
}) => {
  const filterSetId = widget.code_set?.id ?? widget.filter_set?.id ?? null;
  const providerFilterSetId = widget.filter_set?.id ?? null;

  const { data: filterSet, isLoading } = useGetFilterSetByIdQuery(
    { id: String(filterSetId) },
    { skip: !filterSetId }
  );

  const minYear = widget.min_year ?? MIN_VOLUME_YEAR;
  const maxYear = widget.max_year ?? MAX_VOLUME_YEAR;
  const dateRangeLabel = `${minYear} - ${maxYear}`;

  const value = useMemo(
    () => ({
      widget,
      filterSet: filterSet ?? null,
      filterSetId,
      providerFilterSetId,
      isLoading,
      parentLabel,
      parentSummary,
      dateRangeLabel,
      minYear,
      maxYear,
    }),
    [widget, filterSet, filterSetId, providerFilterSetId, isLoading, parentLabel, parentSummary, dateRangeLabel, minYear, maxYear]
  );

  return (
    <WidgetConfigContext.Provider value={value}>
      {children}
    </WidgetConfigContext.Provider>
  );
};
